import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, Crown, Award, Calendar } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";

const ICONS = { sales: TrendingUp, years: Calendar, leader: Crown, mdrt: Award };
const SPANS = {
  0: "lg:col-span-7 lg:row-span-2",
  1: "lg:col-span-5",
  2: "lg:col-span-5",
  3: "lg:col-span-12",
};
const ACCENTS = {
  0: { bg: "bg-orange-600 text-white", inverse: true },
  1: { bg: "bg-white text-slate-900", inverse: false },
  2: { bg: "bg-sky-100 text-slate-900", inverse: false },
  3: { bg: "bg-slate-900 text-white", inverse: true },
};

export default function Achievements() {
  const content = useSiteContent();
  const items = content?.achievements || [];

  return (
    <section data-testid="achievements-section" className="relative py-24 lg:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-12 bg-orange-600" />
              <span className="text-xs tracking-[0.25em] uppercase text-slate-500 font-display">
                {content?.sections?.achievements_label || "Achievements"}
              </span>
            </div>
            <h2 className="font-serif-sc text-4xl lg:text-5xl xl:text-6xl font-bold text-slate-900 leading-tight tracking-tight">
              {content?.sections?.achievements_title_top || "Behind the numbers"}<br />
              <span className="text-slate-500">{content?.sections?.achievements_title_bottom || "lies 15 years of conviction"}</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:pl-8">
            <p className="text-base text-slate-600 leading-relaxed">
              {content?.sections?.achievements_desc || ""}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5">
          {items.map((s, i) => {
            const Icon = ICONS[s.key] || Award;
            const span = SPANS[i] || "lg:col-span-6";
            const accent = ACCENTS[i] || { bg: "bg-white text-slate-900", inverse: false };
            return (
              <motion.div
                key={s.key || i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`stat-${s.key}`}
                className={`relative ${span} ${accent.bg} p-8 lg:p-10 rounded-sm overflow-hidden group transition-transform duration-500 hover:-translate-y-1`}
              >
                <div className="absolute top-6 right-6 opacity-30">
                  <Icon size={28} strokeWidth={1.5} />
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase font-display opacity-70">
                  {s.label}
                </div>
                <div className="font-display font-bold tracking-tighter text-6xl lg:text-7xl xl:text-8xl mt-4 leading-none">
                  {s.value}
                </div>
                <div className={`font-serif-sc text-lg lg:text-xl mt-3 ${accent.inverse ? "opacity-90" : "text-slate-700"}`}>
                  {s.suffix}
                </div>
                <div className={`mt-4 text-sm ${accent.inverse ? "opacity-70" : "text-slate-500"} max-w-md leading-relaxed`}>
                  {s.detail}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
